import type { ICreatePayUseCaseCmd } from "@/data/commands/planning/CreatePayUseCaseCmd";
import type { ICreateUseCaseCmd } from "@/data/commands/planning/CreateUseCaseCmd";
import type { IPaymentMethod, IPredefinedPaymentMethod } from "@/data/model/Customer";
import type { IOrderUseCase } from "@/data/model/OrderUseCase";
import type { IPayUseCase } from "@/data/model/PayUseCase";
import type { ILinkableEntity } from "@/data/model/Shared";
import type { IUseCasePaymentMethod } from "@/data/model/UseCasePaymentMethod";
import type { IUseCasesQry } from "@/data/queries/planning/UseCasesQry";
import { CurrentIdentityService } from "@/data/services/CurrentIdentityService";
import { Format } from "@/data/util/Format";

import {
    BaseServiceDao,
    type IEditableDataSource,
    type IMultiEntityLinkDataSource,
    type IPaginatedResult,
    parseCollection,
    parseMultiLanguageText,
    parsePaginatedResult,
} from "./BaseServiceDao";

export class PlanningDao extends BaseServiceDao {
    public static className = "PlanningDao";

    constructor() {
        super("planning");
        this.isLocationDependent = true;
    }

    //pay use case
    public async getPayUseCase(id: string): Promise<IPayUseCase> {
        return this.get("payusecase/" + id, undefined, PlanningDao.parsePayUseCase);
    }

    public async getPayUseCases(qry: IUseCasesQry, abortSignal?: AbortSignal): Promise<IPaginatedResult<IPayUseCase>> {
        return this.get("payusecase/", qry, (f) => parsePaginatedResult(f, PlanningDao.parsePayUseCase), abortSignal);
    }

    public async createPayUseCase(cmd: ICreatePayUseCaseCmd): Promise<IPayUseCase> {
        return this.post("payusecase/", cmd, PlanningDao.parsePayUseCase);
    }

    public async updatePayUseCase(id: string, cmd: ICreatePayUseCaseCmd): Promise<IPayUseCase> {
        return this.put("payusecase/" + id, cmd, PlanningDao.parsePayUseCase);
    }

    public async removePayUseCase(id: string): Promise<void> {
        return this.delete("payusecase/" + id, {});
    }

    public payUseCaseDataSource: IEditableDataSource<IPayUseCase> = {
        getData: (filter, abortSignal) => this.getPayUseCases(filter, abortSignal),
        getSingle: (a) => this.getPayUseCase(a),
        create: (a) => this.createPayUseCase(a),
        update: (a, b) => this.updatePayUseCase(a, b),
        remove: (a) => this.removePayUseCase(a),
    };

    //article
    public async getUseCase(id: string): Promise<IOrderUseCase> {
        return this.get("usecase/" + id, undefined, PlanningDao.parseUseCase);
    }

    public async getUseCases(qry: IUseCasesQry, abortSignal?: AbortSignal): Promise<IPaginatedResult<IOrderUseCase>> {
        return this.get("usecase/", qry, (f) => parsePaginatedResult(f, PlanningDao.parseUseCase), abortSignal);
    }

    public async createUseCase(cmd: ICreateUseCaseCmd): Promise<IOrderUseCase> {
        return this.post("usecase/", cmd, PlanningDao.parseUseCase);
    }

    public async updateUseCase(id: string, cmd: ICreateUseCaseCmd): Promise<IOrderUseCase> {
        return this.put("usecase/" + id, cmd, PlanningDao.parseUseCase);
    }

    public async removeUseCase(id: string): Promise<void> {
        return this.delete("usecase/" + id, {});
    }

    public useCaseDataSource: IEditableDataSource<IOrderUseCase> = {
        getData: (filter, abortSignal) => this.getUseCases(filter, abortSignal),
        getSingle: (a) => this.getUseCase(a),
        create: (a) => this.createUseCase(a),
        update: (a, b) => this.updateUseCase(a, b),
        remove: (a) => this.removeUseCase(a),
    };

    public useCaseSearch: IMultiEntityLinkDataSource = {
        search: (e, t) =>
            this.get("usecase/search", { Exclude: e, Term: t }, (f) =>
                parseCollection(f, PlanningDao.parseLinkableEntity)
            ),
    };

    public static parseLinkableEntity(literal: any): ILinkableEntity {
        return {
            id: literal.id,
            name: Format.translate(literal.name, CurrentIdentityService.getLanguage()),
            appearsIn:
                literal.appearsIn != null
                    ? parseCollection(literal.appearsIn, PlanningDao.parseLinkableEntity)
                    : undefined,
        };
    }

    public static parseUseCase(literal: any): IOrderUseCase {
        return {
            id: literal.id,
            consumptionSite: parseInt(literal.consumptionSite),
            fulfillmentPositionDisplayMode: parseInt(literal.fulfillmentPositionDisplayMode),
            fulfillmentHandling: parseInt(literal.fulfillmentHandling),
            fulfillmentTimeMode: parseInt(literal.fulfillmentTimeMode),
            buyerRequirement: parseInt(literal.buyerRequirement),
            consumptionSiteDisplayMode: parseInt(literal.consumptionSiteDisplayMode),
            usePayment: literal.usePayment,
            usePreorder: literal.usePreorder,
            locationId: literal.locationId,
            name: parseMultiLanguageText(literal.name),
            orderPlacedText: parseMultiLanguageText(literal.orderPlacedText),
            beforeOrderPlacedText: parseMultiLanguageText(literal.beforeOrderPlacedText),
            useOperatorOfferOverview: literal.useOperatorOfferOverview,
            areItemCommentsAllowed: literal.areItemCommentsAllowed,
            useTip: literal.useTip,
            useDailyOrderIncrement: literal.useDailyOrderIncrement,
            paymentMethods: literal?.paymentMethods
                ? parseCollection(literal.paymentMethods, PlanningDao.parseUseCasePaymentMethod)
                : undefined,
            pushNotificationPickUpOrderMessage: parseMultiLanguageText(literal.pushNotificationPickUpOrderMessage),
            enableNotifications: literal.enableNotifications,
            enablePushNotificationsOnAndroid: literal.enablePushNotificationsOnAndroid,
            enablePushNotificationsOnIos: literal.enablePushNotificationsOnIos,
            defaultTipPercent: literal.defaultTipPercent,
            enableSignUpBeforePay: literal.enableSignUpBeforePay,
            pushNotificationPickUpOrderTitle: parseMultiLanguageText(literal.pushNotificationPickUpOrderTitle),
            markUnpaidOrdersOnPrintouts: literal.markUnpaidOrdersOnPrintouts,
            maxPreorderPeriod: literal.maxPreorderPeriod == null ? undefined : literal.maxPreorderPeriod,
            orderPlacedTextIfNotificationsEnabled: parseMultiLanguageText(
                literal.orderPlacedTextIfNotificationsEnabled
            ),
            visibleEndUserProperties: literal.visibleEndUserProperties,
            oneFulfillmentPerOrderMode: parseInt(literal.oneFulfillmentPerOrderMode),
            allowCreatingOrderBills: literal.allowCreatingOrderBills,
            sendEmailConfirmationsForUnpaidOrders: literal.sendEmailConfirmationsForUnpaidOrders,
            sendOrderReminderByPushNotification: literal.sendOrderReminderByPushNotification,
            sendOrderReminderByEmail: literal.sendOrderReminderByEmail,
            orderReminderPushNotificationTitle: parseMultiLanguageText(literal.orderReminderPushNotificationTitle),
            orderReminderPushNotificationMessage: parseMultiLanguageText(literal.orderReminderPushNotificationMessage),
            useVouchersAndDiscounts: literal.useVouchersAndDiscounts,
            promptOperatorsForReceiptAfterPayment: literal.promptOperatorsForReceiptAfterPayment,
        };
    }

    public static parsePayUseCase(literal: any): IPayUseCase {
        return {
            id: literal.id,
            locationId: literal.locationId,
            name: parseMultiLanguageText(literal.name),
            useTip: literal.useTip,
            paymentMethods: literal?.paymentMethods
                ? parseCollection(literal.paymentMethods, PlanningDao.parseUseCasePaymentMethod)
                : undefined,
            defaultTipPercent: literal.defaultTipPercent,
            useVouchersAndDiscounts: literal.useVouchersAndDiscounts,
            promptOperatorsForReceiptAfterPayment: literal.promptOperatorsForReceiptAfterPayment,
        };
    }

    public static parseUseCasePaymentMethod(literal: any): IUseCasePaymentMethod {
        return {
            id: literal.id ? literal.id : undefined,
            type: literal.type,
            mode: parseInt(literal.mode),
            name: literal.name == null ? undefined : literal.name,
            paymentMethodId: literal.paymentMethodId,
            imagePath: literal.imagePath == null ? undefined : literal.imagePath,
            isEnabledForGuests: literal.isEnabledForGuests,
            isEnabledForWaiters: literal.isEnabledForWaiters,
            localizedName: parseMultiLanguageText(literal.localizedName),
            paymentMethodExternalId: literal.paymentMethodExternalId ? literal.paymentMethodExternalId : undefined,
            isCustom: literal.isCustom,
            isQuickPaymentEnabled: literal.isQuickPaymentEnabled,
            isQuickPaymentAvailable: literal.isQuickPaymentAvailable,
            isPaymentDevice: literal.isPaymentDevice,
            revenueEffective: literal.revenueEffective,
        };
    }

    public static parsePaymentMethod(literal: any): IPaymentMethod {
        return {
            id: literal.id ? literal.id : undefined,
            type: literal.type,
            name: literal.name == null ? undefined : literal.name,
            localizedName: {
                de: literal?.localizedName?.de,
                en: literal?.localizedName?.en,
                fr: literal?.localizedName?.fr,
                it: literal?.localizedName?.it,
            },
            mode: parseInt(literal.mode),
            provider: literal.provider == null ? undefined : literal.provider,
            imagePath: literal.imagePath ? literal.imagePath : undefined,
            isCardScheme: literal.isCardScheme,
            displayCollectiveCardIcon: literal.displayCollectiveCardIcon,
            isProviderEditable: literal.isProviderEditable,
            useCollectiveCardIcon: literal.useCollectiveCardIcon,
            isEditEnabled: literal.isEditEnabled,
            isPaymentDevice: literal.isPaymentDevice,
            allowTipOnlyOnDevice: literal.allowTipOnlyOnDevice,
            revenueEffective: literal.revenueEffective,
            isTipAllowed: literal.isTipAllowed,
        };
    }

    public static parsePredefinedPaymentMethod(literal: any): IPredefinedPaymentMethod {
        return {
            id: literal.id ? literal.id : undefined,
            type: literal.type,
            localizedName: {
                de: literal?.localizedName?.de,
                en: literal?.localizedName?.en,
                fr: literal?.localizedName?.fr,
                it: literal?.localizedName?.it,
            },
            mode: parseInt(literal.mode),
            imagePath: literal.imagePath ? literal.imagePath : undefined,
            isCardScheme: literal.isCardScheme,
            displayCollectiveCardIcon: literal.displayCollectiveCardIcon,
            isProviderEditable: literal.isProviderEditable,
            useCollectiveCardIcon: literal.useCollectiveCardIcon,
            isEditEnabled: literal.isEditEnabled,
            isPaymentDevice: literal.isPaymentDevice,
        };
    }
}
